import * as React from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'wouter';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';
import ApiService from '../../Services/ApiService';
import ConfirmDialog from '../ConfirmDialog';
import ShowLogRefueling from './ShowLogRefueling';
import { URL_EXPENSE_IMPORT } from '../../router/constants';
import Form from './form';
import { IValidationRule } from '../../Services/FormikValidatorService';
import LoaderService from '../../Services/LoaderService';
import { ISimpleSelectOption } from '../Forms/ArrayFields/interfaces';

const RESPONSE_OK = 'ok';
const REQUEST_TIMEOUT = 360000; // 6 minutes

export interface IWizardFooterButtons {
  cancel: {
    title: string;
    callback?: () => void;
  };
  back: {
    title: string;
    callback?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  };
  steps: {
    [index: number]: {
      title: string;
      callback: () => void;
    };
  };
}

interface IData {
  disabled: boolean;
  indexId: string;
  placeholder: string;
  readonly: boolean;
  rules: IValidationRule[];
  title: string;
  type: string;
  value: any; // TODO: Create interface for available value types
  values?: any;
  multiple?: boolean;
  subtype?: string;
  sendOnChange?: () => void;
  section?: string | null;
  inputGroup?: string | null;
  groupIndex?: number;
  firstDisabled?: boolean;
  options?: ISimpleSelectOption[];
}

interface IFormWizardStep {
  label: string;
  inputs: string[];
}

interface IComponentProps {
  for: string;
}

export default function WizardNew(props: IComponentProps): JSX.Element {
  const { formatMessage: f } = useIntl();
  const [_, navigate] = useLocation();
  const [inputs, setInputs] = React.useState<IData[]>([]);
  const [currentStep, setCurrentStep] = React.useState<number>(1);
  const [totalSteps, setTotalSteps] = React.useState<number>(0);
  const [steps, setSteps] = React.useState<IFormWizardStep[]>([]);
  const [footerButtons, setFooterButtons] = React.useState<IWizardFooterButtons | null>(null);
  const [records, setRecords] = React.useState<number>(0);
  const [passed, setPassed] = React.useState<number>(0);
  const [failed, setFailed] = React.useState<number>(0);
  const [ignored, setIgnored] = React.useState<number>(0);
  const [duplicities, setDuplicities] = React.useState<number>(0);
  const [isClosed, setIsClosed] = React.useState<boolean>(false);
  const [dateFrom, setDateFrom] = React.useState<string>('');
  const [dateTo, setDateTo] = React.useState<string>('');
  const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
  const [isContinue, setIsContinue] = React.useState<boolean>(false);
  const [isOpenLog, setIsOpenLog] = React.useState<boolean>(false);
  const [isButtonDisable, setIsButtonDisable] = React.useState<boolean>(false);
  const [isValidated, setIsValidated] = React.useState<boolean>(false);
  const [logSummary, setLogSummary] = React.useState<[]>([]);
  const [refuelingImportData, setRefuelingImportData] = React.useState<Record<string, any>>({});
  const [formValues, setFormValues] = React.useState<Record<string, any>>({});

  const nextStep = async (step: number, values: any) => {
    let url = `v1/${props.for}/import/form`;
    values.wizard = {
      currentStep: step,
    };
    let passedValue = false;
    let formValuesConverted;
    // const currentStepIndex = step - 1;
    switch (step) {
      case 2:
        formValuesConverted = values;
        url = `/v1/${props.for}/import/overview`;
        break;
      case 3:
        const tempFormValues = values;
        formValuesConverted = {
          ...tempFormValues,
          expenseImport: {
            ...refuelingImportData,
          },
        };
        url = `/v1/${props.for}/import/add`;
        break;
      default:
        formValuesConverted = values;
        url = `v1/${props.for}/import/form`;
        break;
    }
    LoaderService.showLoader(true);

    const response = await ApiService.post(
      url,
      step === 2 || step === totalSteps ? formValuesConverted : values,
      true,
      REQUEST_TIMEOUT
    );

    if (response.data) {
      if (response.data.form && currentStep !== totalSteps) {
        setInputs(response.data.form.inputs);
        setCurrentStep(response.data.form.wizard.currentStep);
        setSteps(response.data.form.wizard.steps);
        // setCurrentStepInputs(response.data.form.wizard.steps[currentStepIndex].inputs);
      } else if (response.data.expenseImport) {
        const dateFromConverted = dayjs(response.data.expenseImport.accountingTsFrom).format(
          'DD.MM.YYYY HH:mm'
        );
        const dateToConverted = dayjs(response.data.expenseImport.accountingTsTo).format(
          'DD.MM.YYYY HH:mm'
        );
        setRecords(response.data.expenseImport.records);
        setPassed(response.data.expenseImport.passed);
        passedValue = response.data.expenseImport.passed;
        setFailed(response.data.expenseImport.failed);
        setIgnored(response.data.expenseImport.ignored);
        setDuplicities(response.data.expenseImport.duplicities);
        setDateFrom(dateFromConverted);
        setDateTo(dateToConverted);
        setLogSummary(response.data.expenseImport.log);
        setRefuelingImportData(response.data.expenseImport);
      }
    } else {
      setRecords(0);
      setPassed(0);
      setFailed(0);
      setIgnored(0);
      setDuplicities(0);
      setDateFrom('');
      setDateTo('');
      setLogSummary([]);
      setRefuelingImportData({});
    }
    if (step === 2 && !passedValue) {
      setIsButtonDisable(true);
    }
    LoaderService.showLoader(false);
  };

  const handleFavoriteGroupSelect = async (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    event.preventDefault();
    const templateId: any = event.currentTarget.getAttribute('data-item-value');
    if (templateId) {
      const urlFavorite = `/v1/${props.for}/import-template/${templateId}/favorite`;
      LoaderService.showLoader();
      const response = await ApiService.put(urlFavorite, templateId);
      if (response.data && response.data.status === RESPONSE_OK) {
        nextStep(currentStep, formValues);
      }
      LoaderService.showLoader(false);
    }
  };

  const handleDownloadGroupSelect = async (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    event.preventDefault();
    const templateId = event.currentTarget.getAttribute('data-item-value');
    const nameTemplate = event.currentTarget.getAttribute('data-item-name');
    if (templateId) {
      const urlDownload = `/api/v1/${props.for}/import-template/${templateId}/download`;
      LoaderService.showLoader();
      const response = await ApiService.download(urlDownload, '');
      if (response.status === 200) {
        const contentDisposition = response.headers['content-disposition'];
        const contentType = response.headers['content-type'];

        const filenameRegex = /filename="([^"]*)"/;
        const filenameMatch = contentDisposition.match(filenameRegex);
        const filename = filenameMatch ? filenameMatch[1] : nameTemplate;

        const typeRegex = /([^;]*)/;
        const typeMatch = contentType.match(typeRegex);
        const type = typeMatch ? typeMatch[0] : '';

        const blob = new Blob([response.data], { type });
        saveAs(blob, filename);

        console.log('RESP: ', response.data);
        // const type: string = response.headers['content-type'];
        // const blob = new Blob([response.data], { type });
        // saveAs(blob, `${nameTemplate}`);
      }
      LoaderService.showLoader(false);
    }
  };

  const handleClose = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    setIsClosed(true);
  };

  const previousStep = async (step: number, values: any) => {
    const url = `/v1/${props.for}/import/form`;
    // const currentStepIndex = step - 1;
    values.wizard = {
      currentStep: step,
    };
    LoaderService.showLoader();

    const response = await ApiService.post(url, values, true, REQUEST_TIMEOUT);
    if (response.status === 200) {
      setInputs(response.data.form.inputs);
      // setLabels(response.data.form.labels);
      setSteps(response.data.form.wizard.steps);
      setCurrentStep(response.data.form.wizard.currentStep);
      // setCurrentStepInputs(response.data.form.wizard.steps[currentStepIndex].inputs);
    }
    LoaderService.showLoader(false);
    setIsButtonDisable(false);
  };

  const handleBack = async (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    const currentStepValue = currentStep - 1;
    previousStep(currentStepValue, formValues);
  };

  const handleNext = (event?: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (event) {
      event.preventDefault();
    }

    const nextStepValue = currentStep + 1;
    if (currentStep < totalSteps) {
      setCurrentStep(nextStepValue);
    }

    nextStep(nextStepValue, formValues);
  };

  const handleValidation = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    setIsValidated(true);
  };

  const handleValidationCallback = (value: boolean) => {
    setIsValidated(false);
    if (value) {
      handleNext();
    }
  };

  const handleCancel = () => {
    setIsModalOpen(true);
  };

  React.useEffect(() => {
    async function getForm() {
      const url = `/v1/${props.for}/import/form`;

      LoaderService.showLoader();
      const response = await ApiService.get(url);

      if (response.status === 200) {
        // const currentStepIndex = response.data.form.wizard.currentStep - 1;

        const footerButtonsValues = {
          cancel: {
            title: f({ id: 'refuelings.wizard.buttons.cancel' }),
            callback: handleCancel,
          },
          back: {
            title: f({ id: 'refuelings.wizard.buttons.back' }),
            callback: handleBack,
          },
          steps: {},
        };

        response.data.form.wizard.steps.map((item: any, index: number) => {
          footerButtonsValues.steps = {
            ...footerButtonsValues.steps,
            [index + 1]: {
              title: item.label,
              callback: handleNext,
            },
          };
        });

        footerButtonsValues.steps = {
          ...footerButtonsValues.steps,
          3: {
            title: f({ id: 'refuelings.wizard.buttons.finish' }),
            callback: handleClose,
          },
        };

        setFooterButtons(footerButtonsValues);
        setInputs(response.data.form.inputs);
        // setLabels(response.data.form.labels);
        // setSections(response.data.form.sections);
        setCurrentStep(response.data.form.wizard.currentStep);
        setSteps(response.data.form.wizard.steps);
        // setCurrentStepInputs(response.data.form.wizard.steps[currentStepIndex].inputs);
        setTotalSteps(response.data.form.wizard.steps.length);
        setRecords(0);
        setPassed(0);
        setFailed(0);
        setIgnored(0);
        setDuplicities(0);
        // setIsFavorite(false);
      }

      LoaderService.showLoader(false);
    }

    getForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChangeCallback = async (data: any) => {
    data.wizard = {
      currentStep,
    };

    setFormValues(data);

    LoaderService.showLoader();

    const url = `v1/${props.for}/import/form`;

    const response = await ApiService.post(url, data, true, REQUEST_TIMEOUT);

    if (response.data && response.data.status === RESPONSE_OK) {
      if (response.data.form && currentStep !== totalSteps) {
        setInputs(response.data.form.inputs);
        setCurrentStep(response.data.form.wizard.currentStep);
        setSteps(response.data.form.wizard.steps);
        // setCurrentStepInputs(response.data.form.wizard.steps[1].inputs);
      } else if (response.data.expenseImport) {
        const dateFromConverted = dayjs(response.data.expenseImport.accountingTsFrom).format(
          'DD.MM.YYYY HH:mm'
        );
        const dateToConverted = dayjs(response.data.expenseImport.accountingTsTo).format(
          'DD.MM.YYYY HH:mm'
        );

        setRecords(response.data.expenseImport.records);
        setPassed(response.data.expenseImport.passed);
        setFailed(response.data.expenseImport.failed);
        setIgnored(response.data.expenseImport.ignored);
        setDuplicities(response.data.expenseImport.duplicities);
        setDateFrom(dateFromConverted);
        setDateTo(dateToConverted);
        setLogSummary(response.data.expenseImport.log);
        setRefuelingImportData(response.data.expenseImport);
      }
    } else {
      setRecords(0);
      setPassed(0);
      setFailed(0);
      setIgnored(0);
      setDuplicities(0);
      setDateFrom('');
      setDateTo('');
      setLogSummary([]);
      setRefuelingImportData({});
    }

    if (currentStep === 2 && passed === 0) {
      setIsButtonDisable(true);
    }
    LoaderService.showLoader(false);
  };

  const handleBackToRefuelingImport = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    setIsContinue(true);
  };

  const getStepClass = (step: number) => {
    if (step < currentStep) {
      return 'done';
    }
    if (step === currentStep) {
      return 'active';
    }
    return 'disabled';
  };

  const handleConfirmCancel = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | KeyboardEvent
  ) => {
    event.preventDefault();
    setIsModalOpen(false);
  };

  const handleLog = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    setIsOpenLog(true);
  };

  const handleCancelLog = () => {
    setIsOpenLog(false);
  };

  const logNumber = Number(ignored) + Number(duplicities) + Number(failed);

  return (
    <section className="row row--size-sm">
      {isClosed && navigate(URL_EXPENSE_IMPORT)}
      {isContinue && navigate(`${URL_EXPENSE_IMPORT}/?wizardclose=true`)}

      {isModalOpen && (
        <ConfirmDialog
          title={f({
            id: 'wizard.confirm.dialog.header.title',
          })}
          cancelCallback={handleConfirmCancel}
          continueCallback={handleBackToRefuelingImport}
          cancelButtonTitle={f({
            id: 'wizard.confirm.dialog.button.back',
          })}
          continueButtonTitle={f({
            id: 'wizard.confirm.dialog.button.cancel',
          })}
        >
          {f({
            id: 'wizard.confirm.dialog.message',
          })}
        </ConfirmDialog>
      )}

      {isOpenLog && logNumber > 0 && (
        <ShowLogRefueling
          title={f({ id: 'log.refueling.title' })}
          cancelButtonTitle={f({ id: 'form.close' })}
          cancelCallback={handleCancelLog}
          data={logSummary}
        />
      )}

      <section className="col-xl-12 col-xxxl-6">
        <div className="mt-8">
          {steps && (
            <div className="dropdown">
              <a
                className="e-wizard-item dropdown-toggle"
                type="button"
                id="dropdownMenu1"
                data-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
              >
                {steps[currentStep - 1] && `${currentStep} - ${steps[currentStep - 1].label}`}
              </a>

              <div className="dropdown-menu dropdown-menu-wizard mb-2">
                <div className="b-wizard">
                  {steps &&
                    steps.map((step: any, index: number) => (
                      <div key={`step-${index + 1}`}>
                        <div className={`item ${getStepClass(index + 1)}`}>
                          <span className="count">{index + 1}</span> {step.label}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}
        </div>
        <section className="b-box-wizard border-top-on">
          <section className="p-3 p-xl-8">
            {currentStep !== 2 && currentStep !== totalSteps ? (
              <Form
                data={inputs}
                onChangeCallback={onChangeCallback}
                isValidated={isValidated}
                validationCallback={handleValidationCallback}
                handleFavoriteGroupSelect={handleFavoriteGroupSelect}
                handleDownloadGroupSelect={handleDownloadGroupSelect}
              />
            ) : currentStep === totalSteps ? (
              <section className="b-box border-top-on">
                <div className="p-3 p-xl-8">
                  <div className="b-rte " style={{ textAlign: 'center' }}>
                    <p>
                      {`${f({
                        id: 'wizard.file.refueling.import.first.text',
                      })}`}
                    </p>
                    <p>
                      <strong>
                        {f({
                          id: 'wizard.file.refueling.import.bold.text',
                        })}
                      </strong>{' '}
                      {f({
                        id: 'wizard.file.refueling.import.part1',
                      })}
                      <strong>
                        <i>
                          {` ${f({
                            id: 'wizard.file.refueling.import.bold.text2',
                          })}.`}
                        </i>
                      </strong>
                    </p>
                  </div>
                </div>
              </section>
            ) : (
              <section className="b-box border-top-on p-xl-3">
                <div className="p-3">
                  <div className="font-weight-bold fs-6">
                    {f({
                      id: 'wizard.file.analysis.title',
                    })}
                  </div>
                  <div className="mt-3">
                    <strong>
                      {' '}
                      {f({
                        id: 'wizard.file.analysis.period',
                      })}
                    </strong>{' '}
                    {dateFrom && dateFrom} - {dateTo && dateTo}
                  </div>

                  <div
                    className="row row--size-sm"
                    style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                  >
                    <div className="col-12 col-lg-auto text-center mr-1">
                      <div className="e-rounded-stats mx-auto mt-5">{records && records}</div>
                      <div className="mt-1 font-weight-bold">
                        {f({
                          id: 'wizard.file.analysis.bubble.total.text',
                        })}
                      </div>
                    </div>
                    <div className="col-12 col-lg-auto text-center mr-1">
                      <div className="e-rounded-stats mx-auto green mt-5">{passed && passed}</div>
                      <div className="mt-1 font-weight-bold text-c-green">
                        {f({
                          id: 'wizard.file.analysis.bubble.passed.text',
                        })}
                      </div>
                    </div>

                    {failed ? (
                      <div className="col-12 col-lg-auto text-center mr-1">
                        <div className="e-rounded-stats mx-auto red mt-5">{failed}</div>
                        <div className="mt-1 font-weight-bold text-c-red">
                          {f({
                            id: 'wizard.file.analysis.bubble.failed.text',
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="col-12 col-lg-auto text-center mr-1">
                        <div className="e-rounded-stats mx-auto gray-light mt-5">{failed}</div>
                        <div className="mt-1 font-weight-bold text-gray-light">
                          {f({
                            id: 'wizard.file.analysis.bubble.failed.text',
                          })}
                        </div>
                      </div>
                    )}

                    {duplicities ? (
                      <div className="col-12 col-lg-auto text-center mr-1">
                        <div className="e-rounded-stats mx-auto red mt-5">{duplicities}</div>
                        <div className="mt-1 font-weight-bold text-c-red">
                          {f({
                            id: 'wizard.file.analysis.bubble.duplicities.text',
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="col-12 col-lg-auto text-center mr-1">
                        <div className="e-rounded-stats mx-auto gray-light mt-5">{duplicities}</div>
                        <div className="mt-1 font-weight-bold text-gray-light">
                          {f({
                            id: 'wizard.file.analysis.bubble.duplicities.text',
                          })}
                        </div>
                      </div>
                    )}

                    {ignored ? (
                      <div className="col-12 col-lg-auto text-center">
                        <div className="e-rounded-stats mx-auto red mt-5">{ignored}</div>
                        <div className="mt-1 font-weight-bold text-c-red">
                          {f({
                            id: 'wizard.file.analysis.bubble.ignored.text',
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="col-12 col-lg-auto text-center">
                        <div className="e-rounded-stats mx-auto gray-light mt-5">{ignored}</div>
                        <div className="mt-1 font-weight-bold text-gray-light">
                          {f({
                            id: 'wizard.file.analysis.bubble.ignored.text',
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div
                  className="p-3"
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div className="">
                    {f({
                      id: 'wizard.file.analysis.text',
                    })}
                    <strong className="text-c-green">
                      {' '}
                      {f({
                        id: 'wizard.file.analysis.green.text',
                      })}
                    </strong>{' '}
                    {f({
                      id: 'wizard.file.analysis.lasttext',
                    })}
                  </div>

                  <div>
                    {logNumber === 0 ? (
                      <button disabled={true} className="e-button ">
                        {f({
                          id: 'wizard.file.analysis.log.button',
                        })}
                      </button>
                    ) : (
                      <a
                        href="#"
                        title={f({
                          id: 'wizard.file.analysis.log.button',
                        })}
                        type="button"
                        className="e-button"
                        onClick={handleLog}
                        style={{ borderColor: '#b50436', display: 'flex' }}
                      >
                        {f({
                          id: 'wizard.file.analysis.log.button',
                        })}
                        <span
                          className="e-rounded-stats"
                          style={{
                            marginLeft: '5px',
                            width: '20px',
                            height: '20px',
                            color: '#fff',
                            background: '#b50436',
                            border: '1px solid #b50436',
                            fontSize: '13px',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {`${logNumber}`}
                        </span>
                      </a>
                    )}
                  </div>
                </div>
              </section>
            )}
          </section>

          <div className="modal-footer clearfix">
            {currentStep !== 1 && currentStep !== totalSteps && footerButtons && (
              <a
                href="#"
                data-cy="wizard-footerButtons-back"
                title={footerButtons.back.title}
                type="button"
                className="e-button e-button--gray pull-left"
                onClick={handleBack}
              >
                {footerButtons.back.title}
              </a>
            )}
            {footerButtons && steps && (
              <div>
                {currentStep !== totalSteps && (
                  <a
                    href="#"
                    data-cy="wizard-footerButtons-cancel"
                    title={footerButtons.cancel.title}
                    type="button"
                    className="e-button"
                    onClick={handleCancel}
                  >
                    {footerButtons.cancel.title}
                  </a>
                )}

                {isButtonDisable ? (
                  <button className="e-button e-button--red" disabled={true}>
                    {f({ id: 'wizard.next.step.button' })}
                  </button>
                ) : (
                  <a
                    href="#"
                    data-cy={
                      currentStep !== totalSteps
                        ? 'wizard-next-step-button'
                        : 'wizard-total-step-button'
                    }
                    title={
                      currentStep !== totalSteps
                        ? f({ id: 'wizard.next.step.button' })
                        : footerButtons.steps[totalSteps].title
                    }
                    type="button"
                    className="e-button e-button--red"
                    onClick={
                      currentStep === 1
                        ? handleValidation
                        : currentStep < totalSteps
                          ? handleNext
                          : handleClose
                    }
                  >
                    {currentStep !== totalSteps
                      ? f({ id: 'wizard.next.step.button' })
                      : footerButtons.steps[totalSteps].title}
                  </a>
                )}
              </div>
            )}
          </div>
        </section>
      </section>
    </section>
  );
}
